import {
  type AccountExpoAccountPositionValue, type AccountExpoExposureAsset,
  type AccountExpoOwnerPositionValue, type AccountExposureData
} from '../../../models/exposure/exposure-types'
import { type ExposureDataGridRow } from '../../../pages/exposure/exposure-data-grid'

const DELIMITER = '&&'

export function extractForHeaderInitialExposure (accountExposureData: AccountExposureData): Record<string, string> {
  const results: Record<string, string> = {}
  const accountExpoExposureAsset: AccountExpoExposureAsset[] = accountExposureData.exposure_asset

  if (accountExpoExposureAsset != null) {
    const coinInitialExposureSums: Record<string, number> = {}

    for (let i = 0; i < accountExpoExposureAsset.length; i++) {
      const item = accountExpoExposureAsset[i]
      const { coin } = item
      const initExposure = item.init_amt

      if (Number.isFinite(coinInitialExposureSums[coin])) {
        coinInitialExposureSums[coin] += Number(initExposure)
      } else {
        coinInitialExposureSums[coin] = Number(initExposure)
      }
    }

    Object.keys(coinInitialExposureSums)
      .forEach((coin) => {
        results[coin] = coinInitialExposureSums[coin].toString()
      })
  }

  return results
}

export function createAccountExposureRows (accountExposureData: AccountExposureData): ExposureDataGridRow[] {
  const accountExpoExposureAsset: AccountExpoExposureAsset[] = accountExposureData.exposure_asset
  const accountExpoOwnerPositionValue: AccountExpoOwnerPositionValue[] = accountExposureData.owner_positionvalue
  const accountExpoAccountPositionValue: AccountExpoAccountPositionValue[] = accountExposureData.account_positionvalue

  const uniqueOwnersAccountExposureSums: Record<string, number> = {}
  const uniqueOwnersNetUsdSums: Record<string, number> = {}

  for (let i = 0; i < accountExpoExposureAsset.length; i++) {
    const item = accountExpoExposureAsset[i]
    const { owner } = item
    const currPrice = item.curr_price
    const diffPrice = item.diff_price
    const tag = owner

    if (Number.isFinite(uniqueOwnersAccountExposureSums[tag])) {
      uniqueOwnersAccountExposureSums[tag] += Number(currPrice)
      uniqueOwnersNetUsdSums[tag] += Number(diffPrice)
    } else {
      uniqueOwnersAccountExposureSums[tag] = Number(currPrice)
      uniqueOwnersNetUsdSums[tag] = Number(diffPrice)
    }
  }

  const uniqueOwnerPositionValues: Record<string, string> = {}

  for (let i = 0; i < accountExpoOwnerPositionValue.length; i++) {
    const item = accountExpoOwnerPositionValue[i]
    const tag = item.owner

    uniqueOwnerPositionValues[tag] = item.position_value
  }

  const accountRows: ExposureDataGridRow[] = Object.keys(uniqueOwnersAccountExposureSums)
    .map((owner) => {
      return {
        id: owner,
        path: [owner],
        coin: '',
        account: owner,
        account_type: '',
        symbol: '',
        account_exposure: '',
        price_usd: '',
        exposure_coin: '',
        exposure_usd: uniqueOwnersAccountExposureSums[owner].toString(),
        initial_exposure_coin: '',
        net_coin: '',
        net_usd: uniqueOwnersNetUsdSums[owner].toString(),
        position_value: uniqueOwnerPositionValues[owner],
      }
    })

  const uniqueOwnerTypeNetUsdSums: Record<string, number> = {}

  for (let i = 0; i < accountExpoExposureAsset.length; i++) {
    const item = accountExpoExposureAsset[i]
    const { owner } = item
    const diffPrice = item.diff_price
    const accountType = item.account_type
    const tag = `${String(owner)}${DELIMITER}${String(accountType)}`

    if (Number.isFinite(uniqueOwnerTypeNetUsdSums[tag])) {
      uniqueOwnerTypeNetUsdSums[tag] += Number(diffPrice)
    } else {
      uniqueOwnerTypeNetUsdSums[tag] = Number(diffPrice)
    }
  }

  const accountTypeRows: ExposureDataGridRow[] = Object.keys(uniqueOwnerTypeNetUsdSums)
    .map((ownerAccountType) => {
      const split = ownerAccountType.split(DELIMITER)
      const owner = String(split[0])
      const accountType = String(split[1])

      return {
        id: ownerAccountType,
        path: [owner, accountType],
        coin: '',
        account: '',
        account_type: accountType,
        symbol: '',
        account_exposure: '',
        price_usd: '',
        exposure_coin: '',
        exposure_usd: '',
        initial_exposure_coin: '',
        net_coin: '',
        net_usd: uniqueOwnerTypeNetUsdSums[ownerAccountType].toString()
      }
    })

  const coinPrice: Record<string, string> = {}
  const uniqueOwnerTypeCoinExposureCoinSums: Record<string, number> = {}
  const uniqueOwnerTypeCoinAccountExposureSums: Record<string, number> = {}
  const uniqueOwnerTypeCoinInitialExposureCoinSums: Record<string, number> = {}
  const uniqueOwnerTypeCoinNetCoinSums: Record<string, number> = {}
  const uniqueOwnerTypeCoinNetUsdSums: Record<string, number> = {}

  for (let i = 0; i < accountExpoExposureAsset.length; i++) {
    const item = accountExpoExposureAsset[i]
    const { amount, coin, difference, owner } = item
    const accountType = item.account_type
    const currPrice = item.curr_price
    const diffPrice = item.diff_price
    const initExposure = item.init_amt
    const tag = `${String(owner)}${DELIMITER}${String(accountType)}${DELIMITER}${String(coin)}`

    if (Number.isFinite(uniqueOwnerTypeCoinAccountExposureSums[tag])) {
      uniqueOwnerTypeCoinExposureCoinSums[tag] += Number(amount)
      uniqueOwnerTypeCoinAccountExposureSums[tag] += Number(currPrice)
      uniqueOwnerTypeCoinInitialExposureCoinSums[tag] += Number(initExposure)
      uniqueOwnerTypeCoinNetCoinSums[tag] += Number(difference)
      uniqueOwnerTypeCoinNetUsdSums[tag] += Number(diffPrice)
    } else {
      uniqueOwnerTypeCoinExposureCoinSums[tag] = Number(amount)
      uniqueOwnerTypeCoinAccountExposureSums[tag] = Number(currPrice)
      uniqueOwnerTypeCoinInitialExposureCoinSums[tag] = Number(initExposure)
      uniqueOwnerTypeCoinNetCoinSums[tag] = Number(difference)
      uniqueOwnerTypeCoinNetUsdSums[tag] = Number(diffPrice)
    }

    if (!Object.hasOwn(coinPrice, coin)) {
      coinPrice[coin] = item.price
    }
  }

  const uniqueOwnerTypeCoinPositionValues: Record<string, string> = {}

  for (let i = 0; i < accountExpoAccountPositionValue.length; i++) {
    const item = accountExpoAccountPositionValue[i]
    const { coin, owner } = item
    const accountType = item.account_type
    const tag = `${String(owner)}${DELIMITER}${String(accountType)}${DELIMITER}${String(coin)}`

    uniqueOwnerTypeCoinPositionValues[tag] = item.position_value
  }

  const accountTypeCoinRows: ExposureDataGridRow[] = Object.keys(uniqueOwnerTypeCoinAccountExposureSums)
    .map((ownerAccountTypeCoin) => {
      const split = ownerAccountTypeCoin.split(DELIMITER)
      const owner = String(split[0])
      const accountType = String(split[1])
      const coin = String(split[2])

      return {
        id: ownerAccountTypeCoin,
        path: [owner, accountType, coin],
        coin,
        account: '',
        account_type: '',
        symbol: '',
        account_exposure: '',
        price_usd: coinPrice[coin],
        exposure_coin: uniqueOwnerTypeCoinExposureCoinSums[ownerAccountTypeCoin].toString(),
        exposure_usd: uniqueOwnerTypeCoinAccountExposureSums[ownerAccountTypeCoin].toString(),
        initial_exposure_coin: uniqueOwnerTypeCoinInitialExposureCoinSums[ownerAccountTypeCoin].toString(),
        net_coin: uniqueOwnerTypeCoinNetCoinSums[ownerAccountTypeCoin].toString(),
        net_usd: uniqueOwnerTypeCoinNetUsdSums[ownerAccountTypeCoin].toString(),
        position_value: uniqueOwnerTypeCoinPositionValues[ownerAccountTypeCoin]
      }
    })

  return accountRows
    .concat(accountTypeRows)
    .concat(accountTypeCoinRows)
    .concat(createAccountTypeCoinSymbolRows(accountExpoExposureAsset))
}

function createAccountTypeCoinSymbolRows (accountExposureAsset: AccountExpoExposureAsset[]): ExposureDataGridRow[] {
  return accountExposureAsset.map(item => {
    return {
      id: `${String(item.owner)}${DELIMITER}${String(item.account_type)}${DELIMITER}${String(item.coin)}${DELIMITER}${String(item.symbol_name)}`,
      path: [item.owner, item.account_type, item.coin, item.symbol_name],
      coin: '',
      account: '',
      account_type: '',
      symbol: item.symbol_name,
      account_exposure: item.amount,
      price_usd: '',
      exposure_coin: item.amount,
      exposure_usd: item.curr_price,
      initial_exposure_coin: item.init_amt,
      net_coin: item.difference,
      net_usd: item.diff_price
    }
  })
}
